/**
 * SupportWidget - Widget de suporte UaizyChat integrado ao sistema
 * 
 * Renderiza o widget de chat em todas as páginas do tenant,
 * identificando automaticamente o usuário logado.
 * 
 * A inbox é configurada dinamicamente via Admin → Configurações.
 * 
 * NOTA: Usa lazy loading para evitar duplicação de dependências no bundle
 * e problemas de memória durante o build.
 * 
 * @author Sistema
 * @version 1.2.0 - 2026-06-05 — Oculta widget quando há Sheet/Dialog aberto
 *                                (evita sobreposição com controles na borda direita).
 * @version 1.1.0
 */

import { lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSupportWidgetConfig } from '@/hooks/useSupportWidgetConfig';
import { useHasOpenOverlay } from '@/hooks/useHasOpenOverlay';
import { createLogger } from '@/lib/logger';

// Lista de rotas onde o widget deve ser ocultado
// Evita conflito com áreas de input de chat existentes e oculta o widget
// em TODAS as páginas públicas/externas (visitantes não devem ver suporte interno).
// O check usa `startsWith`, então prefixos cobrem todas as sub-rotas.
const HIDDEN_ROUTES = [
  '/tenant/chat',
  '/schedule',
  '/chat/',
  '/r/',              // Landing pública de captação de indicações (afiliados)
  '/afiliado',        // Login, cadastro, aguardando aprovação e painel externo do afiliado
  '/wl',              // Painel Admin White Label (operadores WL não usam suporte interno do tenant)
  '/select-context',  // Tela de escolha entre painel WL e tenant (pré-sessão)
];

// Logger específico para este componente
const logger = createLogger('SupportWidget');

// Lazy load do Widget para code splitting e redução de memória no build
const Widget = lazy(() => import('@/widget/Widget').then(m => ({ default: m.Widget })));

/**
 * Componente que renderiza o widget de suporte interno
 * 
 * Condições para exibição:
 * - Widget habilitado nas configurações
 * - Inbox configurada
 * - Usuário autenticado
 */
export function SupportWidget() {
  const { user, profile } = useAuth();
  const { config, isLoading } = useSupportWidgetConfig();
  const location = useLocation();
  // Detecta Sheets/Dialogs abertos globalmente para ocultar o widget enquanto durar.
  const hasOpenOverlay = useHasOpenOverlay();
  
  // Não renderiza enquanto carrega
  if (isLoading) {
    return null;
  }
  
  // Não renderiza se widget desabilitado ou sem inbox configurada
  if (!config.enabled || !config.inbox_id) {
    logger.debug('Widget desabilitado ou sem inbox configurada');
    return null;
  }
  
  // Não renderiza se usuário não está logado
  if (!user) {
    return null;
  }
  
  // Não renderiza na página de chat (conflito com área de input de áudio)
  if (HIDDEN_ROUTES.some(route => location.pathname.startsWith(route))) {
    logger.debug('Widget oculto na rota: ' + location.pathname);
    return null;
  }

  // Oculta enquanto houver Sheet/Dialog aberto (Radix marca body[data-scroll-locked]).
  if (hasOpenOverlay) {
    return null;
  }

  
  logger.debug('Renderizando widget com inbox: ' + config.inbox_id);
  
  return (
    <Suspense fallback={null}>
      <Widget
        config={{
          inboxId: config.inbox_id,
          // SEGURANÇA: Usar user.id como visitor_id garante isolamento por usuário
          // O backend valida que visitor_id === user.id quando há token JWT
          visitorId: user.id,
          visitor: {
            name: profile?.full_name || user.email?.split('@')[0] || 'Usuário',
            email: user.email || undefined,
          },
        }}
      />
    </Suspense>
  );
}
